import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, WooCommerceAuthentication as WooCommerceAuthType } from '@/types';
import { apiService } from '@/utils/api';

const WooCommerceAuthentication: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    site_url: '',
    consumer_key: '',
    consumer_secret: ''
  });
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const queryClient = useQueryClient();

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const sitesData = await apiService.getSites();
        setSites(sitesData);
        if (sitesData.length > 0) {
          setSelectedSiteId(sitesData[0].id);
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };
    fetchSites();
  }, []);

  // Fetch WooCommerce auth settings
  const { data: authData, isLoading } = useQuery<WooCommerceAuthType>(
    ['woocommerce-auth', selectedSiteId],
    () => apiService.getWooCommerceAuthentication(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Handle form data population when authData changes
  useEffect(() => {
    if (selectedSiteId && authData !== undefined) {
      // Only set form data if we have actual auth data (not empty/null)
      if (authData && authData.id !== undefined && authData.id !== null) {
        setFormData({
          site_id: authData.site_id?.toString() || '',
          site_url: authData.site_url || '',
          consumer_key: authData.consumer_key || '',
          consumer_secret: authData.consumer_secret || ''
        });
      } else {
        // No existing data, set empty form for POST request
        setFormData({
          site_id: selectedSiteId.toString(),
          site_url: '',
          consumer_key: '',
          consumer_secret: ''
        });
      }
    }
  }, [authData, selectedSiteId]);

  // Create/Update mutation
  const saveMutation = useMutation(
    (data: WooCommerceAuthType) => {
      if (authData && authData.id !== undefined && authData.id !== null) {
        return apiService.updateWooCommerceAuthentication(selectedSiteId!, data);
      } else {
        return apiService.createWooCommerceAuthentication(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setShowSuccess(true);
        queryClient.invalidateQueries(['woocommerce-auth', selectedSiteId]);
        setTimeout(() => setShowSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteWooCommerceAuthentication(selectedSiteId!),
    {
      onSuccess: () => {
        setShowDeleteSuccess(true);
        setFormData({
          site_id: selectedSiteId?.toString() || '',
          site_url: '',
          consumer_key: '',
          consumer_secret: ''
        });
        queryClient.invalidateQueries(['woocommerce-auth', selectedSiteId]);
        setTimeout(() => setShowDeleteSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: WooCommerceAuthType = {
      ...formData,
      site_id: parseInt(formData.site_id)
    };
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these settings?')) {
      deleteMutation.mutate();
    }
  };

  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSiteId = parseInt(e.target.value);
    setSelectedSiteId(newSiteId);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Loading...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">WooCommerce Authentication</h1>

      {/* Success Messages */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                WooCommerce Authentication Settings saved successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {showDeleteSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                WooCommerce Authentication Settings deleted successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Something went wrong. Please try again.
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Site Selection */}
          <div>
            <label htmlFor="site_id" className="block text-sm font-medium text-gray-700 mb-2">
              Site
            </label>
            <select
              id="site_id"
              name="site_id"
              value={selectedSiteId || ''}
              onChange={handleSiteChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Site URL */}
          <div>
            <label htmlFor="site_url" className="block text-sm font-medium text-gray-700 mb-2">
              URL
            </label>
            <input
              type="url"
              id="site_url"
              name="site_url"
              value={formData.site_url}
              onChange={handleInputChange}
              placeholder="https://your-store.com"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              required
            />
          </div>

          {/* Consumer Key */}
          <div>
            <label htmlFor="consumer_key" className="block text-sm font-medium text-gray-700 mb-2">
              Consumer Key
            </label>
            <input
              type="text"
              id="consumer_key"
              name="consumer_key"
              value={formData.consumer_key}
              onChange={handleInputChange}
              placeholder="ck_..."
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              required
            />
          </div>

          {/* Consumer Secret */}
          <div>
            <label htmlFor="consumer_secret" className="block text-sm font-medium text-gray-700 mb-2">
              Consumer Secret
            </label>
            <input
              type="password"
              id="consumer_secret"
              name="consumer_secret"
              value={formData.consumer_secret}
              onChange={handleInputChange}
              placeholder="cs_..."
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save WooCommerce Settings'}
            </button>

            {authData && authData.id !== undefined && authData.id !== null && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default WooCommerceAuthentication;

